import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { useTranslations } from "@vocab/react";
import { MenuRenderer, Box, IconLocation, MenuItemLink, IconTick, Strong, Columns, Column, Inline, Text, Stack, Divider, IconPlatformAndroid, IconPlatformApple, IconSocialFacebook, IconSocialInstagram, IconSocialTwitter, IconSocialYouTube } from "braid-design-system";
import { useCallback, useRef } from "react";
import { useSiteConfigContext, loadUnifiedLocalisation, constructCountryList, AUTHENTICATION_TYPES, generateConstructAnzUrlHelper, SiteConfigContextProvider, PageBlock } from "./styles/ApacCandidateHeader/ApacCandidateHeader.mjs";
import { ApacCandidateHeader } from "./styles/ApacCandidateHeader/ApacCandidateHeader.mjs";
import { useLink, ChevronIconWrapper, CommonContextProvider, Accordion } from "./styles/ApacCandidateFooter/components/Accordion/Accordion.mjs";
import { LinkProvider, DATA_HEADER_FOOTER_LINK_CONTEXT_KEY, isHeaderFooterLink, linkContextDestructor, mapLinkActionHelper } from "./styles/ApacCandidateFooter/components/Accordion/Accordion.mjs";
import { translations as translations$1 } from "./ApacCandidateFooter/.vocab/index.mjs";
import { FooterLink } from "./styles/ApacCandidateFooter/components/FooterLink/FooterLink.mjs";
import { translations } from "./ApacCandidateFooter/components/LowerFooter/.vocab/index.mjs";
import { FooterSegment } from "./styles/ApacCandidateFooter/components/FooterSegment/FooterSegment.mjs";
import { translations as translations$2 } from "./.vocab/index.mjs";
const CountrySwitcher = ({ list, activeCountryText }) => {
  const { sendLinkEvent, injectLinkContextToData } = useLink();
  return /* @__PURE__ */ jsx(
    MenuRenderer,
    {
      placement: "top",
      width: "medium",
      offsetSpace: "small",
      onOpen: () => {
        sendLinkEvent({
          linkContext: {
            openingOrClosing: "opening",
            source: activeCountryText
          }
        });
      },
      onClose: ({ reason }) => {
        if (reason === "exit") {
          sendLinkEvent({
            linkContext: {
              openingOrClosing: "closing",
              source: activeCountryText
            }
          });
        }
      },
      data: { automation: "country-switcher-menu" },
      trigger: (triggerProps, { open }) => /* @__PURE__ */ jsx(Box, { userSelect: "none", cursor: "pointer", ...triggerProps, children: /* @__PURE__ */ jsx(
        ChevronIconWrapper,
        {
          icon: IconLocation,
          label: activeCountryText,
          size: "small",
          upDirectionCondition: !open
        }
      ) }),
      reserveIconSpace: true,
      children: list.map(({ isActive, text, href, linkContext: linkContext2 }, index) => /* @__PURE__ */ jsx(
        MenuItemLink,
        {
          href,
          onClick: () => {
            sendLinkEvent({
              linkContext: { ...linkContext2, isExpanded: true }
            });
          },
          data: injectLinkContextToData(linkContext2, {
            automation: linkContext2.source
          }),
          icon: isActive ? /* @__PURE__ */ jsx(IconTick, {}) : void 0,
          children: isActive ? /* @__PURE__ */ jsx(Strong, { children: text }) : text
        },
        `countrySwitcher_link_${index}`
      ))
    }
  );
};
const LegalLinks = ({ links, space, collapseBelow }) => /* @__PURE__ */ jsx(Columns, { space, collapseBelow, children: links.map((link) => /* @__PURE__ */ jsx(Column, { width: "content", children: /* @__PURE__ */ jsx(FooterLink, { link, size: "xsmall" }) }, link.title)) });
const LowerFooter = ({
  localisations,
  showCountryList,
  isFooterExpanded: isFooterExpandedFlag
}) => {
  const isFooterExpandable = isFooterExpandedFlag !== void 0;
  const isFooterExpanded = isFooterExpandedFlag || !isFooterExpandable;
  const CONTENT_SPACE = "gutter";
  const CONTENT_COLLAPSE_BELOW = isFooterExpanded ? "wide" : "tablet";
  const { list: countryList, activeCountryText } = localisations.countryList;
  const { t } = useTranslations(translations);
  const { brand, brandDisplayName } = useSiteConfigContext();
  return /* @__PURE__ */ jsxs(
    Box,
    {
      display: "flex",
      alignItems: {
        mobile: "center",
        tablet: isFooterExpanded ? "flexStart" : "center",
        wide: "center"
      },
      justifyContent: {
        mobile: "flexStart",
        tablet: showCountryList ? "spaceBetween" : "flexEnd"
      },
      flexDirection: {
        mobile: "row",
        tablet: isFooterExpanded ? "column" : "row",
        wide: "row"
      },
      flexWrap: {
        wide: "wrap"
      },
      children: [
        /* @__PURE__ */ jsx(
          Box,
          {
            display: ["none", showCountryList ? "block" : "none"],
            paddingBottom: {
              tablet: isFooterExpanded ? CONTENT_SPACE : "none",
              wide: "none"
            },
            children: /* @__PURE__ */ jsx(
              CountrySwitcher,
              {
                activeCountryText,
                list: countryList
              }
            )
          }
        ),
        /* @__PURE__ */ jsxs(Inline, { space: CONTENT_SPACE, collapseBelow: CONTENT_COLLAPSE_BELOW, children: [
          isFooterExpanded && /* @__PURE__ */ jsx(
            LegalLinks,
            {
              space: CONTENT_SPACE,
              collapseBelow: CONTENT_COLLAPSE_BELOW,
              links: localisations.legalLinks
            }
          ),
          /* @__PURE__ */ jsx(Text, { size: "xsmall", tone: "secondary", children: t(`copyright ${brand}`, {
            brand: brandDisplayName,
            year: (/* @__PURE__ */ new Date()).getFullYear().toString()
          }) })
        ] })
      ]
    }
  );
};
const UpperFooter = ({ localisations }) => {
  const mapSegment = useCallback(
    (key) => /* @__PURE__ */ jsx(Column, { width: "1/2", children: /* @__PURE__ */ jsx(FooterSegment, { segment: localisations.segments[key] }) }, key),
    [localisations.segments]
  );
  return /* @__PURE__ */ jsxs(Stack, { space: "xlarge", children: [
    /* @__PURE__ */ jsxs(Columns, { collapseBelow: "tablet", space: "medium", children: [
      /* @__PURE__ */ jsx(Column, { width: "1/2", children: /* @__PURE__ */ jsx(Columns, { space: "medium", children: [0, 1].map(mapSegment) }) }),
      /* @__PURE__ */ jsx(Column, { width: "1/2", children: /* @__PURE__ */ jsx(Box, { marginTop: ["small", "none"], children: /* @__PURE__ */ jsx(Columns, { space: "medium", children: [2, 3].map(mapSegment) }) }) })
    ] }),
    /* @__PURE__ */ jsx(Divider, {})
  ] });
};
const linkContext = { source: "partner services" };
const partnerServices = {
  au: {
    linkContext,
    links: [
      {
        title: "Certsy",
        url: "https://certsy.com/",
        linkContext: { source: "certsy" },
        openInNewWindow: true
      },
      {
        title: "GradConnection",
        url: "https://au.gradconnection.com/",
        linkContext: { source: "gradconnection" },
        openInNewWindow: true
      },
      {
        title: "Jora Local",
        url: "https://www.joralocal.com.au/",
        linkContext: { source: "jora local" },
        openInNewWindow: true
      },
      {
        title: "Sidekicker",
        url: "https://sidekicker.com/au/",
        linkContext: { source: "sidekicker" },
        openInNewWindow: true
      },
      {
        title: "GO1",
        url: "https://www.go1.com/",
        linkContext: { source: "go1" },
        openInNewWindow: true
      },
      {
        title: "FutureLearn",
        url: "https://www.futurelearn.com/",
        linkContext: { source: "futurelearn" },
        openInNewWindow: true
      },
      {
        title: "Employment Hero",
        url: "https://employmenthero.com/",
        linkContext: { source: "employment hero" },
        openInNewWindow: true
      },
      {
        title: "JobAdder",
        url: "https://jobadder.com/",
        linkContext: { source: "jobadder" },
        openInNewWindow: true
      }
    ]
  },
  nz: {
    linkContext,
    links: [
      {
        title: "Sidekicker",
        url: "https://sidekicker.com/nz/",
        linkContext: { source: "sidekicker" },
        openInNewWindow: true
      }
    ]
  }
};
const loadPartnerServices = ({
  country = "au"
}) => partnerServices[country];
const usePartners = () => {
  const { brand, urlResolver } = useSiteConfigContext();
  const rootUrl = (expectedBrand) => urlResolver({
    brand: expectedBrand,
    country: null,
    path: "",
    absolute: true,
    language: "en"
  });
  const countryUrl = (country) => urlResolver({
    country,
    path: "",
    absolute: true,
    language: "en"
  });
  return {
    links: [
      {
        title: "Bdjobs (Bangladesh)",
        url: "https://www.bdjobs.com/",
        linkContext: { source: "bdjobs" },
        openInNewWindow: true
      },
      {
        title: "Catho (Brazil)",
        url: "https://www.catho.com.br/",
        linkContext: { source: "catho" },
        openInNewWindow: true
      },
      ...brand !== "jobsdb" ? [
        {
          title: "Jobsdb (SE Asia)",
          url: rootUrl("jobsdb"),
          linkContext: { source: "jobsdb" },
          openInNewWindow: true
        }
      ] : [],
      ...brand !== "jobstreet" ? [
        {
          title: "Jobstreet (SE Asia)",
          url: rootUrl("jobstreet"),
          linkContext: { source: "jobstreet" },
          openInNewWindow: true
        }
      ] : [],
      {
        title: "Jora (Australia)",
        url: "https://au.jora.com/",
        linkContext: { source: "jora au" },
        openInNewWindow: true
      },
      {
        title: "Jora (Worldwide)",
        url: "https://jora.com/",
        linkContext: { source: "jora global" },
        openInNewWindow: true
      },
      {
        title: "OCC Mundial (Mexico)",
        url: "https://www.occ.com.mx/",
        linkContext: { source: "occ mundial" },
        openInNewWindow: true
      },
      ...brand !== "seek" ? [
        {
          title: "SEEK (Australia)",
          url: countryUrl("au"),
          linkContext: { source: "seek" },
          openInNewWindow: true
        },
        {
          title: "SEEK (New Zealand)",
          url: countryUrl("nz"),
          linkContext: { source: "seek" },
          openInNewWindow: true
        }
      ] : []
    ],
    linkContext: { source: "international partners" }
  };
};
const useJobSeekersSegment = ({
  config: { baseUrl, loginUrl },
  links: { job_seekers_segment, seek_sites_link_group, apps_link_group },
  authenticationStatus = AUTHENTICATION_TYPES.UNAUTHENTICATED
}) => {
  const isAuthenticated = authenticationStatus === AUTHENTICATION_TYPES.AUTHENTICATED;
  const constructURL = generateConstructAnzUrlHelper(baseUrl, {
    loginUrl,
    isAuthenticated
  });
  const { t } = useTranslations(translations$1);
  const { t: commonT } = useTranslations(translations$2);
  const { brand, country, brandDisplayName } = useSiteConfigContext();
  return {
    title: t("Job seekers"),
    links: [
      {
        title: commonT("Job search"),
        url: constructURL(job_seekers_segment.job_search_url),
        linkContext: { source: "job search" }
      },
      {
        title: commonT("Profile"),
        url: constructURL(job_seekers_segment.profile_url),
        linkContext: { source: "profile" }
      },
      {
        title: commonT("Recommended jobs"),
        url: constructURL(job_seekers_segment.recommended_jobs_url, {
          requireAuth: true
        }),
        linkContext: { source: "recommended jobs" },
        shouldNoFollow: true
      },
      {
        title: commonT("Saved searches"),
        url: constructURL(job_seekers_segment.saved_searches_url, {
          requireAuth: true
        }),
        linkContext: { source: "saved searches" },
        shouldNoFollow: true
      },
      {
        title: commonT("Saved jobs"),
        url: constructURL(job_seekers_segment.saved_jobs_url, {
          requireAuth: true
        }),
        linkContext: { source: "saved jobs" },
        shouldNoFollow: true
      },
      {
        title: commonT("Applied jobs"),
        url: constructURL(job_seekers_segment.applied_jobs_url, {
          requireAuth: true
        }),
        linkContext: { source: "applied jobs" },
        shouldNoFollow: true
      },
      ...country !== "hk" ? [
        {
          title: commonT("Career advice"),
          url: constructURL(job_seekers_segment.career_advice_url),
          linkContext: { source: "career advice" }
        },
        {
          title: t("Explore careers"),
          url: constructURL(job_seekers_segment.explore_careers_url),
          linkContext: { source: "explore careers" }
        },
        {
          title: t("Explore salaries"),
          url: constructURL(job_seekers_segment.explore_salaries_url),
          linkContext: { source: "explore salaries" }
        }
      ] : [],
      ...country === "au" ? [
        {
          title: commonT("Company reviews"),
          url: constructURL(job_seekers_segment.company_reviews_url),
          linkContext: { source: "company reviews" }
        }
      ] : [],
      {
        title: t("Download apps"),
        linkContext: { source: "download apps" },
        links: [
          {
            title: brand === "seek" ? t("Android") : t("{brand} @ Google Play", {
              brand: brandDisplayName
            }),
            url: apps_link_group.android_app_url,
            iconComponent: IconPlatformAndroid,
            linkContext: { source: "android" },
            openInNewWindow: true,
            shouldNoFollow: true
          },
          {
            title: brand === "seek" ? t("iOS") : t("{brand} @ App Store", {
              brand: brandDisplayName
            }),
            url: apps_link_group.ios_app_url,
            iconComponent: IconPlatformApple,
            linkContext: { source: "ios" },
            openInNewWindow: true,
            shouldNoFollow: true
          }
        ]
      },
      ...brand === "seek" ? [
        {
          title: t("{brand} sites", { brand: brandDisplayName }),
          linkContext: { source: "seek sites" },
          links: [
            {
              title: commonT("Employer site"),
              url: seek_sites_link_group.employer_site_url,
              linkContext: { source: "employer site" }
            },
            ...country === "au" ? [
              {
                title: t("{brand} {country}", {
                  brand: brandDisplayName,
                  country: "NZ"
                }),
                url: seek_sites_link_group.nz_site_url,
                linkContext: { source: "seek nz" }
              }
            ] : [
              {
                title: t("{brand} {country}", {
                  brand: brandDisplayName,
                  country: "AU"
                }),
                url: seek_sites_link_group.au_site_url,
                linkContext: { source: "seek au" }
              }
            ],
            ...country === "au" ? [
              {
                title: commonT("Courses"),
                url: constructURL(seek_sites_link_group.courses_url),
                linkContext: { source: "courses" }
              }
            ] : [],
            {
              title: commonT("Businesses for sale"),
              url: seek_sites_link_group.business_for_sale_url,
              linkContext: { source: "business for sale" }
            },
            {
              title: commonT("Volunteering"),
              url: seek_sites_link_group.volunteering_url,
              linkContext: { source: "volunteering" }
            }
          ]
        }
      ] : []
    ]
  };
};
const useEmployersSegment = ({
  links: { employers_segment }
}) => {
  const { t } = useTranslations(translations$1);
  return {
    title: t("Employers"),
    links: [
      {
        title: t("Register for free"),
        url: employers_segment.register_url,
        linkContext: { source: "register for free" },
        openInNewWindow: true
      },
      {
        title: t("Post a job ad"),
        url: employers_segment.post_job_ad_url,
        linkContext: { source: "post a job ad" },
        openInNewWindow: true
      },
      {
        title: t("Products & prices"),
        url: employers_segment.products_and_prices_url,
        linkContext: { source: "products & prices" },
        openInNewWindow: true
      },
      {
        title: t("Customer service"),
        url: employers_segment.customer_service_url,
        linkContext: { source: "customer service" },
        openInNewWindow: true
      },
      {
        title: t("Hiring advice"),
        url: employers_segment.hiring_advice_url,
        linkContext: { source: "hiring Advice" },
        openInNewWindow: true
      },
      {
        title: t("Market insights"),
        url: employers_segment.market_insights_url,
        linkContext: { source: "market insights" },
        openInNewWindow: true
      },
      {
        title: t("Recruitment software partners"),
        url: employers_segment.recruitment_software_partners_url,
        linkContext: { source: "recruitment software partners" },
        openInNewWindow: true
      }
    ]
  };
};
const useAboutSegment = ({
  config: { baseUrl },
  links: { about_segment }
}) => {
  const { country, brand, brandDisplayName } = useSiteConfigContext();
  const partnerServices2 = loadPartnerServices({
    // Using AU config as default for all non-NZ countries
    country: country === "nz" ? "nz" : "au"
  });
  const partners = usePartners();
  const constructURL = generateConstructAnzUrlHelper(baseUrl);
  const { t } = useTranslations(translations$1);
  return {
    title: t("About {brand}", { brand: brandDisplayName }),
    links: [
      {
        title: brand === "seek" ? t("About {brand}", { brand: brandDisplayName }) : t("About us"),
        url: constructURL(about_segment.about_us_url),
        linkContext: { source: "about seek" }
      },
      ...brand === "seek" ? [
        {
          title: t("Newsroom"),
          url: constructURL(about_segment.newsroom_url),
          linkContext: { source: "newsroom" }
        },
        {
          title: t("Investors"),
          url: constructURL(about_segment.investors_url),
          linkContext: { source: "investors" }
        }
      ] : [],
      {
        title: t("Work for {brand}", { brand: brandDisplayName }),
        url: constructURL(about_segment.work_for_us_url),
        linkContext: { source: "work for seek" },
        openInNewWindow: true
      },
      {
        title: t("International partners"),
        links: partners.links,
        linkContext: partners.linkContext
      },
      {
        title: t("Partner services"),
        links: partnerServices2.links,
        linkContext: partnerServices2.linkContext
      }
    ]
  };
};
const useConnectSegment = ({
  config: { baseUrl },
  links: { social_link_group, connect_segment }
}) => {
  const { brand, brandDisplayName } = useSiteConfigContext();
  const constructURL = generateConstructAnzUrlHelper(baseUrl);
  const { t } = useTranslations(translations$1);
  return {
    title: t("Contact"),
    links: [
      {
        title: t("Help centre"),
        url: connect_segment.help_center_url,
        linkContext: { source: "help centre" },
        openInNewWindow: true
      },
      {
        title: t("Contact us"),
        url: constructURL(connect_segment.contact_us_url),
        linkContext: { source: "contact us" }
      },
      {
        title: t("Product & tech blog"),
        url: connect_segment.product_and_tech_blog_url,
        linkContext: { source: "product & tech blog" },
        openInNewWindow: true
      },
      ...brand === "seek" ? [
        {
          title: t("{brand} videos", { brand: brandDisplayName }),
          url: connect_segment.seek_videos_url,
          linkContext: { source: "seek videos" }
        }
      ] : [],
      {
        title: t("Social"),
        linkContext: { source: "social" },
        links: [
          {
            title: t("Facebook"),
            url: social_link_group.facebook_url,
            iconComponent: IconSocialFacebook,
            linkContext: { source: "facebook" },
            openInNewWindow: true
          },
          {
            title: t("Instagram"),
            url: social_link_group.instagram_url,
            iconComponent: IconSocialInstagram,
            linkContext: { source: "instagram" },
            openInNewWindow: true
          },
          {
            title: t("Twitter"),
            url: social_link_group.twitter_url,
            iconComponent: IconSocialTwitter,
            linkContext: { source: "twitter" },
            openInNewWindow: true
          },
          {
            title: t("YouTube"),
            url: social_link_group.youtube_url,
            iconComponent: IconSocialYouTube,
            linkContext: { source: "youtube" },
            openInNewWindow: true
          }
        ]
      }
    ]
  };
};
const useLegalLinks = ({
  config: { baseUrl },
  links: { legal_links }
}) => {
  const constructURL = generateConstructAnzUrlHelper(baseUrl);
  const { t } = useTranslations(translations$1);
  return [
    {
      title: t("Terms & conditions"),
      url: constructURL(legal_links.tnc_url),
      linkContext: { source: "terms & conditions" }
    },
    {
      title: t("Security & Privacy"),
      url: constructURL(legal_links.security_privacy_url),
      linkContext: { source: "security & privacy" }
    }
  ];
};
const useLocalisations = ({
  authenticationStatus
}) => {
  const { country, language, site, urlResolver } = useSiteConfigContext();
  const { Footer: links, Config: config } = loadUnifiedLocalisation({
    language,
    urlResolver,
    site,
    country
  });
  const jobSeekersSegment = useJobSeekersSegment({
    config,
    links,
    authenticationStatus
  });
  const employersSegment = useEmployersSegment({ links });
  const aboutSegment = useAboutSegment({
    config,
    links
  });
  const connectSegment = useConnectSegment({ config, links });
  const legalLinks = useLegalLinks({ config, links });
  const countryList = constructCountryList({
    country,
    urlResolver
  });
  return {
    countryList,
    legalLinks,
    segments: [
      jobSeekersSegment,
      employersSegment,
      aboutSegment,
      connectSegment
    ],
    isSecondaryBackground: true
  };
};
const ApacCandidateFooterView = ({
  authenticationStatus = AUTHENTICATION_TYPES.UNAUTHENTICATED,
  data,
  isExpandedVersion = true,
  showCountryList = true,
  widthMode = "large"
}) => {
  const localisations = useLocalisations({
    authenticationStatus
  });
  const { t } = useTranslations(translations$1);
  const footerRef = useRef(null);
  const onOpenAccordion = useCallback(() => {
    var _a;
    const footerTop = ((_a = footerRef.current) == null ? void 0 : _a.offsetTop) ?? 0;
    const documentBottom = document.body.scrollHeight || document.documentElement.scrollHeight;
    window.scrollTo({
      top: footerTop || documentBottom
    });
  }, []);
  const upperFooter = /* @__PURE__ */ jsx(Box, { paddingTop: "xlarge", children: /* @__PURE__ */ jsx(UpperFooter, { localisations }) });
  const LowerFooter$1 = ({
    isFooterExpanded
  }) => /* @__PURE__ */ jsx(Box, { paddingTop: "large", paddingBottom: "xlarge", children: /* @__PURE__ */ jsx(
    LowerFooter,
    {
      localisations,
      showCountryList,
      isFooterExpanded
    }
  ) });
  const { isSecondaryBackground } = localisations;
  return /* @__PURE__ */ jsx(
    Box,
    {
      position: "relative",
      background: isSecondaryBackground ? "body" : "surface",
      ref: footerRef,
      data: { automation: "footer", ...data },
      children: /* @__PURE__ */ jsx(PageBlock, { width: widthMode, position: "footer", children: isExpandedVersion ? /* @__PURE__ */ jsxs(Fragment, { children: [
        upperFooter,
        /* @__PURE__ */ jsx(LowerFooter$1, {})
      ] }) : /* @__PURE__ */ jsx(
        Accordion,
        {
          callToAction: (isOpen) => /* @__PURE__ */ jsx(LowerFooter$1, { isFooterExpanded: isOpen }),
          onExpendCollapseLinkContextData: { source: "Collapsed Footer" },
          onOpen: onOpenAccordion,
          isInline: true,
          isDirectionUpward: true,
          isCallToActionFullWidth: true,
          actionDisplayText: t("Explore site"),
          isStickyAction: true,
          children: upperFooter
        }
      ) })
    }
  );
};
const ApacCandidateFooter = ({
  language,
  ...props
}) => /* @__PURE__ */ jsx(SiteConfigContextProvider, { language, children: /* @__PURE__ */ jsx(CommonContextProvider, { position: "footer", children: /* @__PURE__ */ jsx(ApacCandidateFooterView, { ...props }) }) });
export {
  ApacCandidateFooter,
  ApacCandidateHeader,
  LinkProvider as ApacCandidateHeaderFooterLinkProvider,
  DATA_HEADER_FOOTER_LINK_CONTEXT_KEY,
  isHeaderFooterLink,
  linkContextDestructor,
  mapLinkActionHelper
};
